/**
 * CloudFront signed cookies for wiki images.
 *
 * Wiki image URLs are stored as relative paths ("/wiki-images/<contextId>/…")
 * and served by the same CloudFront distribution as the portal, with signing
 * enforced. The browser therefore needs the three CloudFront-* cookies before
 * any <img> is fetched, so every view that renders wiki content asks for them
 * first.
 *
 * These two calls go to RELATIVE paths, not REACT_APP_API_URL. CloudFront
 * routes /client/wiki-images/cookies and /admin/wiki-images/cookies to the API,
 * so they are same-origin with the portal and same-host with the images. That
 * matters twice over: the browser stores the Set-Cookie without CORS being
 * involved at all, and no preflight exists to fail. Going via the API's own
 * hostname does not work - api-preview.themigrationexperts.com is a different
 * ORIGIN (host-exact) even though it shares the cookie DOMAIN, so the request
 * is credentialed cross-origin and API Gateway's CORS mock answers the
 * preflight with '*' and no Allow-Credentials, which the browser rejects.
 *
 * Local development (localhost:3001) has no such CloudFront path and is a
 * different origin from the CDN, so wiki images do not render locally once
 * signing is on. Testing is on preview.
 */

// contextId -> epoch seconds the current cookie expires. Re-fetching is cheap
// but pointless on every render, so a live cookie is reused until it is close
// to expiry.
const issued = new Map();
const REFRESH_MARGIN_SECONDS = 300;

function isStillValid(contextId) {
    const expiresAt = issued.get(contextId);
    if (!expiresAt) return false;
    return expiresAt - REFRESH_MARGIN_SECONDS > Math.floor(Date.now() / 1000);
}

/**
 * Cookies for the logged-in client's own project.
 *
 * The project is taken from the token server side, so nothing identifying is
 * sent here - a client can only ever receive cookies for their own images.
 */
export async function ensureClientWikiImageCookies(projectId) {
    if (projectId && isStillValid(projectId)) return true;

    const token = localStorage.getItem('clientToken');
    if (!token) return false;

    try {
        const response = await fetch(
            '/client/wiki-images/cookies',
            {
                method: 'GET',
                credentials: 'same-origin',
                headers: { 'Authorization': `Bearer ${token}` }
            }
        );
        if (!response.ok) {
            console.error('Wiki image cookies refused:', response.status);
            return false;
        }
        const { expiresAt } = await response.json();
        if (projectId) issued.set(projectId, expiresAt);
        return true;
    } catch (err) {
        console.error('Wiki image cookie fetch failed:', err);
        return false;
    }
}

/**
 * Cookies for an admin viewing a specific context.
 *
 * contextId is a projectId when editing a client's wiki, or a template wikiId
 * when editing a template. Each gets its own cookie triplet, scoped by Path, so
 * having several open at once does not overwrite one set with another.
 */
export async function ensureAdminWikiImageCookies(contextId) {
    if (!contextId) return false;
    if (isStillValid(contextId)) return true;

    const token = localStorage.getItem('adminToken');
    if (!token) return false;

    try {
        const response = await fetch(
            `/admin/wiki-images/cookies?contextId=${encodeURIComponent(contextId)}`,
            {
                method: 'GET',
                credentials: 'same-origin',
                headers: { 'Authorization': `Bearer ${token}` }
            }
        );
        if (!response.ok) {
            console.error('Wiki image cookies refused:', response.status);
            return false;
        }
        const { expiresAt } = await response.json();
        issued.set(contextId, expiresAt);
        return true;
    } catch (err) {
        console.error('Wiki image cookie fetch failed:', err);
        return false;
    }
}
